import React from "react"

import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { OneVerifyIntegrationStatus } from "@common-shared/types/integrations.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { MarkTileElement } from "../../elements/markTile.element"
import { type IOneVerifyIntegration } from "../../types/integrations.types"

export const IntegrationsListComponent: React.FunctionComponent<
    IIntegrationsListComponentProps
> = props => {
    return (
        <div
            className={`
                flex flex-wrap gap-[16px]
                ${props.className ?? ""}
            `}
        >
            {props.integrations.map(integration => {
                const isSelected =
                    integration.id === props.selectedIntegration?.id

                const isComingSoon =
                    integration.status === OneVerifyIntegrationStatus.comingSoon

                const isLive =
                    integration.status === OneVerifyIntegrationStatus.live

                return (
                    <ButtonElement
                        key={integration.id}
                        className="min-w-[160px]"
                        htmlType="button"
                        type="default"
                        ariaPressed={isSelected}
                        onClick={() => {
                            props.onSelectIntegration(integration)
                        }}
                        buttonClassName={`
                            !flex !flex-col !items-start !justify-start !text-left
                            !rounded-[8px] !p-[16px] !min-w-[160px] !h-auto !shadow-none
                            !relative
                            border border-solid transition-colors
                            ${
                                isSelected
                                    ? "!border-brand-primary !bg-brand-whitePurple shadow-6"
                                    : "!border-brand-warmLight2 !bg-brand-white hover:!bg-brand-offWhite"
                            }
                        `}
                    >
                        {isComingSoon ? (
                            <PrimaryText
                                className="
                                    absolute top-[12px] right-[12px] z-[1] px-[6px] py-[2px] rounded-[4px]
                                    bg-brand-warmLight2
                                "
                                size={10}
                                lineHeight={14}
                                weight="semibold"
                                color={NIRVANA_COLORS.brand.grey7}
                            >
                                SOON
                            </PrimaryText>
                        ) : null}

                        {isLive ? (
                            <PrimaryText
                                className="
                                    absolute top-[12px] right-[12px] z-[1] px-[6px] py-[2px] rounded-[4px]
                                    bg-brand-greenLight
                                "
                                size={10}
                                lineHeight={14}
                                weight="semibold"
                                color={NIRVANA_COLORS.brand.greenDark}
                            >
                                LIVE
                            </PrimaryText>
                        ) : null}

                        <MarkTileElement
                            className="mb-[12px]"
                            icon={integration.markIcon}
                            backgroundColor={integration.markBackgroundColor}
                            markImageUrl={integration.markImageUrl}
                            markImageFit={integration.markImageFit}
                            tileLabel={integration.name}
                            size="small"
                        />

                        <PrimaryText typography="h6" weight="medium">
                            {integration.name}
                        </PrimaryText>
                    </ButtonElement>
                )
            })}

            <ButtonElement
                className="min-w-[160px]"
                htmlType="button"
                type="default"
                onClick={props.onRequestIntegration}
                buttonClassName="
                    !flex !flex-col !items-start !justify-start !text-left
                    !rounded-[8px] !p-[16px] !min-w-[160px] !h-auto !shadow-none
                    !border !border-dashed !border-brand-warmLight2 !bg-brand-white
                    hover:!bg-brand-offWhite transition-colors
                "
            >
                <div
                    className="
                        mb-[12px] w-[48px] h-[48px] rounded-[8px] flex items-center justify-center
                        border border-solid border-brand-warmLight2 bg-brand-offWhite
                    "
                    aria-hidden
                >
                    <FontAwesomeIcon
                        icon={faPlus}
                        style={{
                            width: 20,
                            height: 20,
                            color: NIRVANA_COLORS.brand.grey7,
                        }}
                    />
                </div>

                <PrimaryText
                    typography="h6"
                    weight="medium"
                    color={NIRVANA_COLORS.brand.grey7}
                >
                    Request an Integration
                </PrimaryText>
            </ButtonElement>
        </div>
    )
}

interface IIntegrationsListComponentProps {
    className?: string
    integrations: IOneVerifyIntegration[]
    selectedIntegration: IOneVerifyIntegration | null
    onSelectIntegration: (integration: IOneVerifyIntegration) => void
    onRequestIntegration: () => void
}
